import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import map from 'lodash/map';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { getAllInvoicesByUserId } from 'pages/MyAccount/services/api/account';
import usePagination from 'pages/MyAccount/hooks/usePagination';
import PageContainer from 'pages/MyAccount/shared-components/PageContainer/PageContainer';
import Pagination from 'pages/MyAccount/shared-components/Pagination/Pagination';
import LoadingLogo from 'pages/MyAccount/shared-components/LoadingLogo/LoadingLogo';
import { scrollToTopGradually } from 'pages/MyAccount/shared-components/ScrollToTopButton/ScrollToTopButton';
import { useUserContext } from '@fff-web/fff-utilities';
import Invoice from './Invoice';

import styles from './InvoiceHistory.module.css';

const INVOICES_PER_PAGE = 10;
const getTotalPages = (totalInvoices) =>
  Math.ceil(totalInvoices / INVOICES_PER_PAGE);

const InvoiceHistory = () => {
  const { userInfo } = useUserContext();
  const userId = userInfo?.user?.userId;
  const [loading, setLoading] = useState(true);
  const [invoices, setInvoices] = useState({
    items: [],
    totalPages: 0,
  });
  const { limit, offset, currentPage, onClickPage } = usePagination({
    INVOICES_PER_PAGE,
  });
  const { items, totalPages } = invoices;
  const { myAccountOrderHistory: myAccountOrderHistoryFF } = useFlags();
  const history = useHistory();
  const slicedInvoices = items.slice(offset, offset + limit);

  const handleClickBack = () => history.push('/my-account/orders');

  useEffect(() => {
    const fetchInvoices = async () => {
      try {
        setLoading(true);
        scrollToTopGradually();

        const allInvoicesRes = await getAllInvoicesByUserId(userId);

        setInvoices({
          items: allInvoicesRes,
          totalPages: getTotalPages(allInvoicesRes.length),
        });
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);
      } finally {
        setLoading(false);
      }
    };
    if (userId) {
      fetchInvoices();
    }
  }, [userId]);

  if (loading) return <LoadingLogo />;

  return (
    <PageContainer>
      <div className={styles.header}>
        {myAccountOrderHistoryFF && (
          <button type="button" onClick={handleClickBack}>
            <i className="far fa-chevron-left" />
            Back to Orders
          </button>
        )}
        <h1>{myAccountOrderHistoryFF ? 'Invoices' : 'Invoice History'}</h1>
      </div>
      <div className={styles.invoices}>
        {map(slicedInvoices, (invoice, index) => {
          const { invoiceNumber } = invoice;
          return (
            <Invoice key={invoiceNumber} tabIndex={index} invoice={invoice} />
          );
        })}
        {totalPages > 1 && (
          <Pagination
            pageCount={totalPages}
            currentPage={currentPage}
            containerClassName={styles.paginationContainer}
            onPageChange={onClickPage}
          />
        )}
      </div>
    </PageContainer>
  );
};

InvoiceHistory.defaultProps = {
  onClickBack: () => {},
};

export default InvoiceHistory;
